import React, { useEffect, useState } from "react";
import {
  HomePageTitle,
  HomePageWrapper,
  HomeContentColumn,
  StyledHomeTabs,
  TabsContentWrapper,
} from "./HomeStyled";
import { useDispatch, useSelector } from "react-redux";
import { fetchMessagesList } from "../../slice/HomeSlice";
import ErrorToast from "../../components/Notification/ErrorToast";
import Loader from "../../components/Loader/Loader";
import { useNavigate } from "react-router-dom";
import TabHeader from "../../components/Home/TabHeader";
import MyWorkTab from "./MyWork/MyWorkTab";
import MessagesTab from "./MyTasks/MessagesTab";
import CaseListTab from "./CaseList/CaseListTab";
import GenericTabContainer from "../../components/common/GenericTabContainer";
import { getRowProps, getMessageThreadOpener, handleErrorFactory, handleAPICalls } from "./HomeUtils";
import { checkSession } from "../../slice/AuthSlice";
import MobileHome from "./MobileHome";
import useIsMobile from "../../hooks/useIsMobile";
import MessageThreadModal from "../../components/Messages/MessageThreadModal";

const Home = () => {
  const isMobile = useIsMobile();
  // Phones get the "Today" dashboard (greeting / search / work cards /
  // review cards / activity) — the desktop review tables clip and
  // side-scroll at phone widths. Fork before the desktop hooks mount,
  // same pattern as Inbox.js.
  return isMobile ? <MobileHome /> : <DesktopHome />;
};

/**
 * Home is the workbench, not a directory (Matthew, 2026-08-28: "free up
 * this screen to be a real work tool — pull stuff off the stack and do
 * some work"). Three tabs:
 *
 *   - My Work — the stack. Everything in the user's court (work items,
 *     doc reviews, signatures, tasks — they're all just work) with
 *     in-place verbs, plus what they're waiting on others for.
 *   - Messages — the second inbox: conversations, not work routing.
 *   - Case List — the lookup tool.
 *
 * The former My Tasks / Document Signature tabs are subsumed into My
 * Work ("those are just work items"); Send to VA moved to its own page
 * behind a left-sidebar icon (/va-submissions) — it's a submission
 * console, not part of anyone's daily stack.
 */
const DesktopHome = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [activeMainTab, setActiveMainTab] = useState("myWork");
  const [showNotification, setShowNotification] = useState(false);
  const [error, setError] = useState([]);
  const { messagesList } = useSelector((state) => state.home);
  const { sessionChecked } = useSelector((state) => state.auth);

  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [openMessageThreadRecord, setOpenMessageThreadRecord] = useState(null);
  const [unreadMessages, setUnreadMessages] = useState(0);

  const handleError = handleErrorFactory(setIsLoading, setError, setShowNotification);
  const callAPICalls = handleAPICalls(dispatch, setIsLoading, {
    fetchMessagesList,
  });

  useEffect(() => {
    if (!sessionChecked) {
      dispatch(checkSession())
    }
    // The only Home-owned fetch left is Messages (for the unread badge);
    // My Work and Case List fetch their own data inside their tabs.
    const loadInitialData = async () => {
      setIsInitialLoad(true);
      try {
        await callAPICalls("messages");
      } catch (error) {
        handleError(error?.message || "Failed to initialize data");
      } finally {
        setIsInitialLoad(false);
      }
    };
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, sessionChecked]);

  useEffect(() => {
    if (messagesList) {
      setUnreadMessages((prev) => messagesList?.filter_chips?.unread ?? prev);
    }
  }, [messagesList]);

  const handleMainTabChange = (tab) => {
    setActiveMainTab(tab);
    if (tab === "messages") {
      dispatch(fetchMessagesList())
    }
    // My Work and Case List fetch their own data inside the tab components.
  };

  const rowProps = getRowProps(navigate, dispatch, handleError);
  // Review rows tag the navigation with `autoOpenReviewNotes: true` so
  // TabView pops the chronological comment thread on arrival rather than
  // making the reviewer hunt for it in the More menu.
  const reviewRowProps = getRowProps(navigate, dispatch, handleError, {
    navState: { autoOpenReviewNotes: true },
  });
  // Opening a message used to always navigate away to the matter
  // workspace - slow, and it dumped the reader on the matter home page
  // instead of the conversation. Now a click opens it in place, the way
  // an email client opens a message without leaving the inbox.
  // "Open in matter workspace" (inside the modal) keeps the old
  // navigate-based flow for anyone who wants the full document tools.
  const openMessageThreadInMatter = getMessageThreadOpener(navigate, dispatch);
  const closeMessageThreadModal = () => {
    setOpenMessageThreadRecord(null);
    // Refresh so the unread dot/count on the row (and the tab badge)
    // reflect the read-receipt the modal just posted.
    dispatch(fetchMessagesList());
  };

  const renderTabContent = () => {
    if (activeMainTab === "messages") {
      return (
        <GenericTabContainer
          activeTab="messages"
          handleTabChange={() => {}}
          isLoading={isLoading}
          isInitialLoad={isInitialLoad}
          getRowProps={rowProps}
          setError={setError}
          setShowNotification={setShowNotification}
          handleError={handleError}
          setIsLoading={setIsLoading}
          handleAPICalls={callAPICalls}
          statusOptions={[]}
          dueOnOptions={[]}
          tabs={[]}
          tabComponents={{
            messages: MessagesTab,
            props: { messagesList, onOpenThread: setOpenMessageThreadRecord },
          }}
          bellClickConfig={{ handleBellClick: () => {} }}
          chipCountConfig={{
            messages: (chip) => messagesList?.filter_chips?.[chip] || 0,
          }}
        />
      );
    }

    if (activeMainTab === "caseList") {
      return <CaseListTab handleError={handleError} />;
    }

    return (
      <MyWorkTab
        getRowProps={rowProps}
        getReviewRowProps={reviewRowProps}
      />
    );
  };

  return (
    <HomePageWrapper>
      {isInitialLoad && isLoading && <Loader />}
      <HomeContentColumn>
        <HomePageTitle>Home</HomePageTitle>

      <StyledHomeTabs
        activeKey={activeMainTab}
        onChange={handleMainTabChange}
        items={[
          {
            key: "myWork",
            label: (
              // No counts here on purpose (Matthew, 2026-08-31: totals are
              // noise people learn to ignore). Messages keeps its unread
              // count — a small badge on the label; zero unread renders
              // no badge at all.
              <TabHeader
                heading="My Work"
                active={activeMainTab === "myWork"}
                widgets={[]}
              />
            ),
          },
          {
            key: "messages",
            label: (
              <TabHeader
                heading="Messages"
                active={activeMainTab === "messages"}
                widgets={[
                  {
                    heading: "Unread",
                    count: unreadMessages,
                  },
                ]}
              />
            ),
          },
          {
            key: "caseList",
            label: (
              <TabHeader
                heading="Case List"
                active={activeMainTab === "caseList"}
                widgets={[]}
              />
            ),
          },
        ]}
      />

      </HomeContentColumn>

      <TabsContentWrapper>
        {renderTabContent()}
      </TabsContentWrapper>
      <MessageThreadModal
        open={Boolean(openMessageThreadRecord)}
        thread={openMessageThreadRecord}
        onClose={closeMessageThreadModal}
        onOpenInMatter={(thread) => {
          setOpenMessageThreadRecord(null);
          openMessageThreadInMatter(thread);
        }}
      />
      {showNotification && (
        <ErrorToast
          message={error}
          onClose={(id) =>
            setError((prev) => prev.filter((msg) => msg.id !== id))
          }
        />
      )}
    </HomePageWrapper>
  );
};

export default Home;
